import React from 'react';
import { Box, Chip, Typography, useTheme, useMediaQuery } from '../ui';

interface SelectToggleOption {
  value: string;
  label: string;
}

interface SelectToggleProps {
  label: string;
  options: SelectToggleOption[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | undefined;
  name?: string;
}

const SelectToggle: React.FC<SelectToggleProps> = ({ label, options, value, onChange, disabled = false, error = false, errorMessage, name }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5}}>
      {/* Hidden input for DOM access and form submission compatibility */}
      {name && <input type="hidden" name={name} value={value || ''} />}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          fontSize: isMobile ? '14px' : '16px',
          lineHeight: isMobile ? '20px' : '22px',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2.25, flexWrap: 'wrap' }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            onClick={() => !disabled && onChange?.(option.value)}
            disabled={disabled}
            sx={{
              border: error 
                ? `2px solid ${theme.palette.error.main}` 
                : value === option.value 
                  ? `1.5px solid #003D40`
                  : `1px solid #E1E5E5`,
              backgroundColor: value === option.value ? "#E8EFEE" : '#ffffff',
              color: value === option.value ? '#003D40' : "#939495",
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontWeight: 400,
              fontFamily: 'Roboto',
              letterSpacing: '0%',
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: isMobile ? '16px' : '20px',
              borderRadius: isMobile ? '8px' : '8px',
              '&:hover': {
                backgroundColor: disabled
                  ? (value === option.value ? theme.palette.primary.main : 'white')
                  : (value === option.value ? '#E8EFEE' : '#ffffff'),
              },
            }}
          />
        ))}
      </Box>

      {/* Error message - matching TextField.tsx error handling pattern */}
      {(error || errorMessage) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'block',
          }}
        >
          {errorMessage || 'This field is required'}
        </Typography>
      )}
    </Box>
  );
};

export default SelectToggle;
